import { WORKOUTS, WEEKLY_PATTERN, registerWorkoutLibrary } from './workouts'
import { SUPPLEMENTS_DAILY, D3_SUPPLEMENT, getD3Dose, registerSupplementLibrary } from './diet'
import { getMenu, registerMenuLibrary } from './menus'
import { computeMealTotals, itemToGroceryDelta, formatItemLabel } from './foods'

// Overrides the bundled default workouts/menus/supplements in place from a
// Firestore-sourced plan doc (see lib/hooks.js's usePlanLibrary) - called
// once from Health.jsx's AppShell, same pattern as data/foods.js's
// registerDishes(). Unlike the dish/exercise libraries, the plan has a
// complete, correct bundled fallback (this file's own PHASES/PLAN_START_DATE
// plus workouts.js/menus.js/diet.js), so this is a best-effort override, not
// a loading gate - if Firestore is unreachable the app just keeps using the
// bundled plan.
export function registerPlanLibrary(planLib) {
  if (!planLib) return
  if (planLib.workouts && planLib.weeklyPattern) {
    registerWorkoutLibrary({ workouts: planLib.workouts, weeklyPattern: planLib.weeklyPattern })
  }
  if (planLib.menus) registerMenuLibrary(planLib.menus)
  if (planLib.dailySupplements && planLib.d3Supplement) {
    registerSupplementLibrary({ dailySupplements: planLib.dailySupplements, d3Supplement: planLib.d3Supplement })
  }
}

export const TOTAL_DAYS = 90

// Day 1 of the plan is fixed to Monday 28 Sep 2026 in the source plan (every
// menu/workout/date in the calendar is anchored to this).
export const PLAN_START_DATE = '2026-09-28'

// Every phase trains the SAME 7-day Push/Pull/Legs/Upper/Lower/Full Body/
// Arms & Core rotation (see data/workouts.js) - train every day, no rest
// days. What changes phase to phase is the daily steps target and how close
// each set is pushed to failure: Foundation leaves 3-4 reps in the tank,
// Build pushes to 1-2 reps left and adds a set where you can, Intensify
// takes the last set of each exercise near failure.
export const PHASES = [
  { id: 1, name: 'Foundation', weeks: '1-4', startDay: 1, endDay: 28, split: '7-day rotation - learn the lifts, 3-4 reps in reserve', stepsTarget: 8000 },
  { id: 2, name: 'Build', weeks: '5-8', startDay: 29, endDay: 56, split: '7-day rotation - add weight, 1-2 reps left', stepsTarget: 9000 },
  { id: 3, name: 'Intensify', weeks: '9-13', startDay: 57, endDay: 90, split: '7-day rotation - peak effort, near failure', stepsTarget: 10000 },
]

export function getPhase(dayNumber) {
  return PHASES.find((p) => dayNumber >= p.startDay && dayNumber <= p.endDay)
}

export function getWeekInPhase(dayNumber, phase) {
  return Math.floor((dayNumber - phase.startDay) / 7) + 1
}

export function getDayPlan(dayNumber) {
  const phase = getPhase(dayNumber)
  // Day 1 = Monday, so this index is 0=Mon...6=Sun for every phase (each
  // phase starts on a Monday, so phase-relative and global weekday indices
  // are the same mod 7).
  const weekdayIndex = (dayNumber - 1) % 7
  const pattern = WEEKLY_PATTERN[phase.id]
  const workoutKey = pattern[weekdayIndex]
  const isCardioDay = workoutKey === 'rec'
  const workout = WORKOUTS[isCardioDay ? 'cardio_core' : workoutKey]

  const menuNumber = weekdayIndex + 1
  const menu = getMenu(menuNumber)

  const supplements = [...SUPPLEMENTS_DAILY]
  if (getD3Dose(dayNumber)) supplements.push(D3_SUPPLEMENT)

  return {
    day: dayNumber,
    phase,
    week: getWeekInPhase(dayNumber, phase),
    weekday: weekdayIndex + 1,
    workoutKey,
    workoutLabel: workout.label,
    isActiveRecovery: isCardioDay,
    exercises: workout.exercises,
    menuNumber,
    menuName: menu.name,
    meals: menu.meals,
    dailyTotals: { kcal: menu.totalKcal, protein: menu.totalProtein },
    supplements,
    stepsTarget: phase.stepsTarget,
  }
}

const aiExerciseWatchUrl = (name) => `https://www.youtube.com/results?search_query=${encodeURIComponent(name + ' exercise form')}`

// Same return shape as getDayPlan(), but sourced from a user's AI-generated
// plan doc (see lib/aiPlan.js) instead of the static WORKOUTS/MENUS data.
// Phase boundaries and weekly training-day counts are kept identical to the
// static plan on purpose, so this only differs in *which* exercises/foods
// fill each slot - every page that already consumes getDayPlan()'s shape
// works unchanged.
export function getAiDayPlan(aiPlan, dayNumber) {
  const phase = getPhase(dayNumber)
  const weekdayIndex = (dayNumber - 1) % 7
  const pattern = aiPlan.weeklyPattern[phase.id]
  const workoutKey = pattern[weekdayIndex]
  const isCardioDay = workoutKey === 'rec'
  const workout = aiPlan.workouts[workoutKey] || aiPlan.workouts.rec
  const exercises = (workout.exercises || []).map((e) => ({ ...e, watchUrl: aiExerciseWatchUrl(e.name) }))

  const menuNumber = weekdayIndex + 1
  const rawMenu = aiPlan.menus[menuNumber]
  const meals = rawMenu.meals.map((m) => {
    const totals = computeMealTotals(m.items)
    return {
      key: m.key,
      time: m.time,
      label: m.label,
      items: m.items.map((i) => formatItemLabel(i)),
      ingredients: m.items.map(itemToGroceryDelta).filter(Boolean),
      ...totals,
    }
  })

  const supplements = [...SUPPLEMENTS_DAILY]
  if (getD3Dose(dayNumber)) supplements.push(D3_SUPPLEMENT)

  return {
    day: dayNumber,
    phase,
    week: getWeekInPhase(dayNumber, phase),
    weekday: weekdayIndex + 1,
    workoutKey,
    workoutLabel: workout.label,
    isActiveRecovery: isCardioDay,
    exercises,
    menuNumber,
    menuName: rawMenu.name,
    meals,
    dailyTotals: {
      kcal: meals.reduce((s, m) => s + m.kcal, 0),
      protein: Math.round(meals.reduce((s, m) => s + m.protein, 0) * 10) / 10,
    },
    supplements,
    stepsTarget: phase.stepsTarget,
  }
}

// Every page reads "today's plan" through this so an AI-personalized user
// transparently gets their own plan while everyone else keeps the static one.
export function getEffectiveDayPlan(dayNumber, aiPlan) {
  return aiPlan ? getAiDayPlan(aiPlan, dayNumber) : getDayPlan(dayNumber)
}

export function getDateForDay(dayNumber) {
  const start = new Date(PLAN_START_DATE + 'T00:00:00')
  start.setDate(start.getDate() + (dayNumber - 1))
  return start
}

export function formatDayDate(dayNumber) {
  const d = getDateForDay(dayNumber)
  const weekday = d.toLocaleDateString('en-US', { weekday: 'short' }).toUpperCase()
  const day = d.getDate()
  const month = d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase()
  return `${weekday} ${day} ${month}`
}

export function getAllDays() {
  const days = []
  for (let d = 1; d <= TOTAL_DAYS; d++) days.push(getDayPlan(d))
  return days
}
